import { useMutation } from '@tanstack/react-query';
import WidgetApiV2 from '../../lib/api/widget/WidgetApiV2';
import { EventWidgetRequestPayloadV2, WIDGET_TYPE_V2, WidgetRequestPayload } from '../../types/widget';
import useRedirect from '../common/useRedirect';
import { WIDGET_ALL_LIST_URL_PATH } from '../../constants/service/common/urlConstants';
import { ErrorResponseType, extractErrorMessage } from '../../lib/utils/checkErrorResponse';
import toastMsg from '../../lib/toastMsg';
import { confirmWidgetPeriod } from '../../lib/utils/widget/confirmWidgetPeriod';

interface UseEditWidgetReturnType {
  editWidget: (payload: { type: WIDGET_TYPE_V2; widget: WidgetRequestPayload }) => void;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

const useWidgetEdit = (id: number): UseEditWidgetReturnType => {
  const redirect = useRedirect();
  const { mutate, isLoading, isSuccess, isError } = useMutation(
    (payload: { type: WIDGET_TYPE_V2; widget: WidgetRequestPayload }) => WidgetApiV2.editWidget(id, payload),
    {
      onSuccess: () => redirect(WIDGET_ALL_LIST_URL_PATH),
      onError: (error: ErrorResponseType) => toastMsg.error(extractErrorMessage(error)),
    },
  );

  const editWidget = async (payload: { type: WIDGET_TYPE_V2; widget: WidgetRequestPayload }): Promise<void> => {
    const widget = payload.widget;
    const isEventWidget = widget instanceof EventWidgetRequestPayloadV2;

    const confirmResult = await confirmWidgetPeriod({
      startDate: isEventWidget ? null : widget.exposureStartDate,
      endDate: isEventWidget ? null : widget.exposureEndDate,
    });

    if (confirmResult) mutate(payload);
  };

  return {
    editWidget,
    isLoading,
    isSuccess,
    isError,
  };
};

export default useWidgetEdit;
